/**
 * @file DouleurHancheRetourSport.tsx
 * @summary Article de blog — DouleurHancheRetourSport
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, QuandConsulter, AdaptationChistera, AdaptationTable, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/douleur-hanche-retour-sport";
const TITLE = "Douleur à la hanche et retour au sport : conflit, tendinopathie, prothèse";
const DESC = "Conflit fémoro-acétabulaire, tendinopathie du moyen fessier, post-prothèse de hanche : adapter sa pratique sportive sans aggraver. Approche kiné par CrossFit Chistera.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Le squat profond est-il dangereux pour la hanche ?", a: "Non, en soi. Mais une morphologie de hanche qui ferme l'angle (CAM ou pincer) peut générer un conflit douloureux en fin d'amplitude. Dans ce cas, on adapte la profondeur et on travaille la mobilité hanche en parallèle, plutôt que de forcer." },
  { q: "Peut-on faire du CrossFit avec une prothèse totale de hanche ?", a: "Oui, à partir de 6-12 mois post-opératoire selon l'avis du chirurgien. Le CrossFit est même excellent pour la longévité de la prothèse (renforcement, stabilité). On évite simplement les mouvements à risque luxation : flexion >90° avec rotation interne, hyper-flexion avec adduction." },
  { q: "Mes hanches craquent quand je fais des squats. C'est grave ?", a: "Non, dans la grande majorité des cas. Les craquements sans douleur sont bénins (souvent un tendon qui passe sur une saillie osseuse). Si c'est associé à une douleur ou à un blocage, là on consulte." },
  { q: "Combien de temps pour récupérer d'une tendinopathie du moyen fessier ?", a: "8-16 semaines avec un protocole de renforcement adapté (abductions hanche, ponts, monster walks). Plus long si pris en charge tardivement (>6 mois de douleur)." },
  { q: "Je sens un pincement à la hanche en bas du squat. Faut-il arrêter ?", a: "Pas forcément. On réduit l'amplitude (box squat à hauteur tolérée), on travaille la mobilité hanche (90/90, pigeon, hip airplane), et on progresse progressivement. Si la douleur persiste ou s'aggrave, avis kiné." },
  { q: "Le running aggrave-t-il une douleur de hanche ?", a: "Souvent oui pour la tendinopathie du moyen fessier, surtout si la cadence est basse et la foulée longue. On peut maintenir le cardio avec assault bike ou rameur le temps de la rééducation." },
];

const adaptations = [
  { standard: "Back squat / front squat profond", adapted: "Box squat à hauteur tolérée, ou goblet squat" },
  { standard: "Pistols / shrimp squats", adapted: "Bulgarian split squats" },
  { standard: "Lunges / step-ups dynamiques", adapted: "Reverse lunges contrôlés, charge réduite" },
  { standard: "Box jumps", adapted: "Box step-ups" },
  { standard: "Sprints courts", adapted: "Assault bike ou rameur" },
  { standard: "Burpees", adapted: "Step-down burpees" },
  { standard: "Wall balls profonds", adapted: "Front squats légers + lancers séparés" },
  { standard: "American kettlebell swings", adapted: "Russian swings (mi-hauteur)" },
];

export default function DouleurHancheRetourSport() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Douleur à la hanche : reprendre et maintenir le sport"
      breadcrumbLabel="Douleur à la hanche"
      category="Sport santé · Douleur ciblée"
      author={author} reviewer={reviewer}
      publishedAt="10 novembre 2026" updatedAt="10 novembre 2026" readingMinutes={9}
      ctaVariant="douleur" needsHealthDisclaimer
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-11-10", author, reviewer, faq })}
    >
      <EnBref>
        <p>
          La hanche est une articulation profonde, stable, mais souvent négligée dans la mobilité. Trois grandes causes de douleur en sport adulte : le <strong>conflit fémoro-acétabulaire</strong>, la <strong>tendinopathie du moyen fessier</strong>, et l'<strong>arthrose / post-prothèse</strong>.
        </p>
        <p>
          La majorité de ces situations permettent de continuer le sport en adaptant. Le repos complet est rarement la solution — au contraire, il fragilise les muscles péri-articulaires qui stabilisent la hanche.
        </p>
      </EnBref>

      <h2>Anatomie rapide : pourquoi la hanche fait mal</h2>
      <p>La hanche est une articulation sphérique très contrainte : la tête fémorale s'emboîte dans l'acétabulum (cotyle). Trois éléments expliquent la majorité des douleurs :</p>
      <p><strong>La morphologie osseuse.</strong> Une excroissance sur la tête fémorale (CAM) ou un cotyle trop couvrant (pincer) peut générer un conflit en fin d'amplitude — typiquement en bas du squat profond ou en flexion forcée. C'est anatomique, pas pathologique en soi.</p>
      <p><strong>Les tendons des fessiers.</strong> Le moyen fessier stabilise la hanche dans la marche, la course, les fentes. Sa tendinopathie est très fréquente après 40 ans, surtout chez les femmes.</p>
      <p><strong>Le cartilage articulaire.</strong> L'arthrose de hanche (coxarthrose) est plus précoce qu'on ne le pense — souvent dès 50 ans, parfois plus tôt en cas de morphologie favorisante.</p>

      <h2>Les douleurs de hanche les plus fréquentes</h2>
      <h3>Le conflit fémoro-acétabulaire (CAM / pincer)</h3>
      <p>Douleur en avant de la hanche, "en C" (le pli de l'aine + la face latérale). Aggravée par la position assise prolongée, le squat profond, les fentes profondes. Diagnostic souvent par le test FADIR (flexion-adduction-rotation interne). Approche conservatrice (mobilité, renforcement glutéaux, adaptation des amplitudes) en première intention. Chirurgie en cas d'échec après 6-12 mois.</p>

      <h3>La tendinopathie du moyen fessier</h3>
      <p>Douleur sur la face <em>latérale</em> de la hanche (le grand trochanter). Sensibilité au coucher sur le côté douloureux, douleur en marchant longtemps ou en montant les escaliers. Excellente réponse à un protocole de renforcement progressif des abducteurs (clamshells, monster walks, side-lying hip raises, puis abductions chargées).</p>

      <h3>La coxarthrose débutante</h3>
      <p>Douleur diffuse en avant de la hanche ou irradiation vers le genou. Raideur matinale courte (moins de 30 min). Limitation progressive de la rotation interne. La pratique sportive adaptée <strong>ralentit l'évolution</strong> de l'arthrose, contrairement à l'idée reçue. Mouvement = vie pour le cartilage.</p>

      <h3>Post-prothèse totale de hanche (PTH)</h3>
      <p>Reprise progressive du sport possible à partir de 3-6 mois selon le chirurgien. Le CrossFit est compatible à condition de respecter quelques précautions : pas de flexion supérieure à 90° avec rotation interne et adduction (mouvement à risque luxation). Voir notre <Link to="/blog/reprendre-sport-apres-operation-bas-du-corps">guide complet sur la reprise post-opération bas du corps</Link>.</p>

      <QuandConsulter>
        <p>Avis médical ou kiné sans délai si :</p>
        <ul>
          <li>Douleur après une chute ou un traumatisme</li>
          <li>Boiterie franche au quotidien</li>
          <li>Limitation marquée et soudaine de la mobilité</li>
          <li>Douleur nocturne intense qui ne cède pas</li>
          <li>Sensation d'instabilité ou de blocage articulaire</li>
          <li>Douleur qui irradie en arrière (suspicion lombaire — voir <Link to="/blog/mal-de-dos-sport-crossfit">mal de dos et sport</Link>)</li>
          <li>Fièvre, gonflement, rougeur (suspicion d'infection ou inflammation systémique)</li>
        </ul>
      </QuandConsulter>

      <h2>Adaptations CrossFit pour une hanche sensible</h2>
      <AdaptationTable rows={adaptations} />

      <h2>Le travail à intégrer en parallèle</h2>
      <p><strong>Mobilité hanche</strong> : 90/90, pigeon pose, hip airplane, world's greatest stretch. 5-10 minutes tous les jours.</p>
      <p><strong>Renforcement des stabilisateurs</strong> : clamshells, monster walks, side-lying hip raises, ponts unilatéraux. 3 séries de 12-15 répétitions, 3 fois par semaine.</p>
      <p><strong>Renforcement du tronc</strong> : planches, dead bug, bird dog. Un tronc faible reporte la charge sur la hanche.</p>

      <AdaptationChistera>
        <p>Pour un adhérent avec douleur de hanche :</p>
        <ol>
          <li>Le coach identifie le contexte (post-trauma, chronique, post-op)</li>
          <li>Il adapte les amplitudes et substitue les mouvements à risque</li>
          <li>Il propose un mini-protocole de mobilité + renforcement glutéaux à intégrer en début ou fin de séance</li>
          <li>Si la douleur persiste 4-6 semaines, orientation kiné spécialisé</li>
          <li>Pour les post-PTH : coaching individuel hautement recommandé pour les 6 premiers mois de reprise</li>
        </ol>
      </AdaptationChistera>

      <FAQList items={faq.map((f) => ({ q: f.q, a: <p>{f.a}</p> }))} />

      <FurtherReading items={[
        { label: "Douleur au genou en CrossFit", to: "/blog/douleur-genou-crossfit" },
        { label: "Reprendre le sport après une opération du bas du corps", to: "/blog/reprendre-sport-apres-operation-bas-du-corps" },
        { label: "Faire du sport avec une douleur", to: "/blog/faire-du-sport-avec-douleur" },
        { label: "CrossFit après 60 ans", to: "/blog/crossfit-senior" },
      ]} />

      <SourcesSection items={[
        { label: "Reiman et al. 2018 — Femoroacetabular impingement: a clinical practice guideline (BJSM)", href: "https://bjsm.bmj.com/content/52/10/631" },
        { label: "Mellor et al. 2018 — Education plus exercise versus corticosteroid injection for gluteal tendinopathy (BMJ)", href: "https://www.bmj.com/content/361/bmj.k1662" },
        { label: "Bannuru et al. 2019 — OARSI guidelines for the non-surgical management of knee, hip and polyarticular osteoarthritis", href: "https://www.oarsijournal.com/article/S1063-4584(19)31116-1/fulltext" },
      ]} />
    </ArticleLayout>
  );
}
